import { computePointsContext } from "@/pricing";

export default {
  data(){
    return {
      channel: {
        display_is_open: false,
        order: {
          subtotal: 0,
          grand_total: 0,
          tax_total: 0,
          discount_total: 0,
          order_discount: 0,
          items: [],
          surcharge_value:0
        },
        isSale: false,
        sale: {
          isCash: false,
          transactionAmount: 0.00,
          changeTenderAmount: 0.00
        }
      },
      sendDebounceTimer: null,
      lastSentOrderHash: null,
    };
  },
  methods: {
    sendOrderToChannel(order) {
      try {
        if (order.isSale) {
          this.channel.isSale = true
          this.channel.sale = Object.assign(this.channel.sale, order.sale || order)
          // Send immediately for sales (no debounce)
          this.sendChannelData()
        }
        else {
          this.channel.order = Object.assign(this.channel.order, order)
          this.channel.display_is_open = true
          // Debounce order updates
          this.debouncedSendChannelData()
        }
      } catch (e){
        console.warn('[CustomerDisplay] Failed to prepare order payload', e);
      }
    },
    getRewardsEnabledFlag() {
      try {
        const settings = this.terminal && this.terminal.db_settings ? this.terminal.db_settings : []
        const rewardsSetting = settings.find(s => s && s.name === 'rewards_module')
        return rewardsSetting && rewardsSetting.value === 'true' ? 'true' : 'false'
      } catch (e) {
        return 'false'
      }
    },
    getTerminalDbSetting(name) {
      try {
        const terminal = this.terminal || (this.$store && this.$store.getters && this.$store.getters['config/terminal'])
        const settings = terminal && terminal.db_settings ? terminal.db_settings : []
        return settings.find(s => s && s.name === name) || null
      } catch (e) {
        return null
      }
    },
    // Dual cash/card pricing for the customer display. Sent whenever the
    // terminal's cash_discount program is enabled, regardless of the order's
    // toggle, so the display can always show the rate and both prices. The live
    // total is discounted only while the order toggle is on, so derive the other
    // price from the factor in whichever direction applies.
    getCustomerDisplayCashDiscount(order) {
      const setting = (this.allowCashDiscount) || this.getTerminalDbSetting('cash_discount')
      if (!(setting && setting.value === 'true')) return { cash_discount: false }
      const factor = this.cashDiscountFactor || 1
      const live = ((order && order.grand_total) || 0) + (this.getCustomerDisplaySurchargeValue(order) || 0)
      const applied = !!(order && order.cash_discount)
      const cash = applied ? live : live / factor
      const card = applied ? live * factor : live
      return {
        cash_discount: true,
        cash_discount_applied: applied,
        cash_discount_rate: factor,
        cash_discount_percent: parseFloat(((factor - 1) * 100).toFixed(2)),
        cash_price: parseFloat(cash.toFixed(2)),
        card_price: parseFloat(card.toFixed(2))
      }
    },
    getCustomerDisplaySurchargePercent() {
      try {
        const alwaysShowSetting = this.getTerminalDbSetting('always_show_surcharge')
        if (alwaysShowSetting && alwaysShowSetting.value != null && alwaysShowSetting.value !== '') {
          const alwaysShowPercent = parseFloat(alwaysShowSetting.value)
          if (Number.isFinite(alwaysShowPercent) && alwaysShowPercent > 0) {
            return alwaysShowPercent
          }
        }

        const paymentTypes = this.paymentTypes
          || (this.$store && this.$store.getters && this.$store.getters['config/paymentTypes'])
          || []
        const paymentTypeWithSurcharge = paymentTypes.find(paymentType => paymentType && paymentType.surcharge > 0)
        return paymentTypeWithSurcharge ? paymentTypeWithSurcharge.surcharge : 0
      } catch (e) {
        return 0
      }
    },
    getCustomerDisplaySurchargeValue(order) {
      try {
        const actualSurcharge = parseFloat(order && order.surcharge_value)
        if (Number.isFinite(actualSurcharge) && actualSurcharge > 0) {
          return actualSurcharge
        }

        const grandTotal = parseFloat(order && order.grand_total)
        if (!Number.isFinite(grandTotal) || grandTotal <= 0) {
          return 0
        }

        const percent = this.getCustomerDisplaySurchargePercent()
        if (!percent || percent <= 0) {
          return 0
        }

        return parseFloat((grandTotal * (percent / 100)).toFixed(2))
      } catch (e) {
        return 0
      }
    },
    async sendBlankPayloadToCustomerDisplay() {
      try {
        if (this.sendDebounceTimer) {
          clearTimeout(this.sendDebounceTimer);
          this.sendDebounceTimer = null;
        }

        this.channel.isSale = false;
        this.channel.sale = {
          isCash: false,
          transactionAmount: 0.00,
          changeTenderAmount: 0.00
        };

        this.channel.display_is_open = false;
        this.channel.order = {
          subtotal: 0,
          grand_total: 0,
          tax_total: 0,
          discount_total: 0,
          order_discount: 0,
          items: [],
          surcharge_value: 0
        };

        // Force send even if the previous payload was already blank
        this.lastSentOrderHash = null;
        await this.sendChannelData();
      } catch (e) {
        console.warn('[CustomerDisplay] Failed to send blank payload', e);
      }
    },
    debouncedSendChannelData() {
      if (this.sendDebounceTimer) {
        clearTimeout(this.sendDebounceTimer);
      }
      this.sendDebounceTimer = setTimeout(() => {
        this.sendChannelData();
      }, 300);
    },
    setDisplayIsOpen(display_is_open) {
      try {
        this.channel.display_is_open = display_is_open;
        this.sendChannelData()
      } catch (e) {
        console.warn('[CustomerDisplay] Failed to update display state', e);
      }
    },
    hashPayload(payload) {
      try {
        return JSON.stringify(payload);
      } catch (e) {
        return Date.now().toString();
      }
    },
    async sendChannelData() {
      try {
        // Prepare payload
        let payload;
        const rewards = this.getRewardsEnabledFlag();

        const whitelabel = this.$store.getters['config/whitelabelSettings'];

        if (this.channel.isSale) {
          payload = {
            sale: this.channel.sale,
            isSale: this.channel.isSale,
            local_id: this.channel.sale.local_id || this.channel.sale.id,
            order_id: this.channel.sale.id,
            rewards,
            whitelabel
          };
        } else {
          // Optimize order payload - send only necessary data
          payload = {
            order: this.optimizeOrderPayload(this.channel.order),
            cd_is_open: this.channel.display_is_open,
            isSale: false,
            local_id: this.channel.order.local_id || this.channel.order.id,
            order_id: this.channel.order.id,
            rewards,
            whitelabel
          };
        }

        // Check if order actually changed
        const payloadHash = this.hashPayload(payload);
        if (payloadHash === this.lastSentOrderHash && !this.channel.isSale) {
          return;
        }
        this.lastSentOrderHash = payloadHash;

        await this.$store.dispatch('customerDisplay/sendMessage', payload);
      } catch (e) {
        console.warn('[CustomerDisplay] Failed to send payload', e);
      }
    },
    optimizeOrderPayload(order) {
      // Create minimal payload with only necessary fields
      return {
        subtotal: order.subtotal || 0,
        grand_total: order.grand_total || 0,
        tax_total: order.tax_total || 0,
        discount_total: order.discount_total || 0,
        order_discount: order.order_discount || 0,
        surcharge_value: this.getCustomerDisplaySurchargeValue(order),
        ...this.getCustomerDisplayCashDiscount(order),
        earned_points: computePointsContext(order).earnedPoints,
        notes: order.notes || order.note || '',
        customer: order.customer ? {
          first_name: order.customer.first_name,
          phone: order.customer.phone,
          points_balance: order.customer.rewards_points || order.customer.reward_points || order.customer.points_balance || 0,
          reward_points: order.customer.reward_points || 0,
          rewards_points: order.customer.rewards_points || 0
        } : null,
        items: (order.items || [])
          .filter(item => item && !item.void_status)
          .map(item => ({
            id: item.id,
            name: item.name || item.product_name,
            quantity: item.quantity || 1,
            price: item.price || 0,
            total: item.total || 0,
            discount: item.discount || 0,
            wholeDiscount: item.wholeDiscount || 0,
            promotion_applied: item.promotion_applied || false,
            applied_promotions: item.applied_promotions || [],
            notes: item.notes || '',
            // Include modifiers if present
            modifiers: Array.isArray(item.modifiers)
              ? item.modifiers
                .filter(mod => mod && !mod.void_status)
                .map(mod => ({
                  name: mod.name,
                  price: mod.price
                }))
              : []
          }))
      };
    }
  },
  beforeDestroy() {
    if (this.sendDebounceTimer) {
      clearTimeout(this.sendDebounceTimer);
    }
  }
};
